<template>
    <div class="table-allocation-info" :class="className" v-if="allocation.allocation != null" @click="showContextMenu">
        <template v-if="allocation.isInitializedFullTableAllocationInfo || !showLoadingLabel">
            <span class="table-allocation-time" v-if="showAllocationTime && tableAllocationTimestamp">
                {{ tableAllocationTimestamp }}
            </span>
            <span class="table-allocation-guests">
                <i class="fal fa-user mr-1"></i>
                {{ guestsNumber }}
            </span>
        </template>
        <span v-else>
            {{ translations.general.loading }}
        </span>
        <TableAllocationButtonContextMenu
            ref="contextMenu"
            :showTransferOption="showTransferOption"
            :showAdjustEndUsersCountOption="showAdjustEndUsersCountOption"
            :showShowApplicableDiscountsOption="showShowApplicableDiscountsOption"
            @onRenameSelected="emit('onRenameSelected')"
            @onTransferSelectedItemsSelected="emit('onTransferSelectedItemsSelected')"
            @onCheckoutSelected="emit('onCheckoutSelected')"
            @onPrintSelected="emit('onPrintSelected')"
            @onAdjustEndUsersCountSelected="(count) => emit('onAdjustEndUsersCountSelected', count)"
            @onShowApplicableDiscountsSelected="emit('onShowApplicableDiscountsSelected')"
        />
    </div>
</template>

<script setup lang="ts">
    import { dataEvents } from "@/data/events/dataEvents";
    import type { IBillEvent } from "@/data/model/Events";
    import { RoomPlanTableStatus } from "@/data/model/Operator";
    import { useBillStore } from "@/data/stores/bill";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { translations } from "@/shared/localization/translations";
    import { computed, onBeforeUnmount, onMounted, ref } from "vue";

    import TableAllocationButtonContextMenu from "@/components/tableAllocation/TableAllocationButtonContextMenu.vue";

    const emit = defineEmits<{
        onCheckoutSelected: [];
        onTransferSelectedItemsSelected: [];
        onRenameSelected: [];
        onPrintSelected: [];
        onAdjustEndUsersCountSelected: [count: number];
        onShowApplicableDiscountsSelected: [];
    }>();

    const contextMenu = ref<InstanceType<typeof TableAllocationButtonContextMenu> | null>(null);

    function showContextMenu(e) {
        if (disabled.value) return;

        contextMenu.value?.show(e);
    }

    withDefaults(
        defineProps<{
            showAllocationTime?: boolean;
            showLoadingLabel?: boolean;
            showTransferOption?: boolean;
            showAdjustEndUsersCountOption?: boolean;
            showShowApplicableDiscountsOption?: boolean;
        }>(),
        {
            showAllocationTime: true,
            showLoadingLabel: true,
            showTransferOption: false,
            showAdjustEndUsersCountOption: false,
            showShowApplicableDiscountsOption: false,
        }
    );
    const isInitialized = ref(false);

    const bill = useBillStore();
    const allocation = useTableAllocationStore();

    const disabled = computed(() => bill.currentBill == null && !allocation.hasAnyGuests);
    const className = computed(() => {
        return {
            active: !disabled.value,
            "open-bill": isInitialized.value && allocation.tableStatus == RoomPlanTableStatus.OpenBill,
            "unfulfilled-order": isInitialized.value && allocation.tableStatus == RoomPlanTableStatus.UnfulfilledOrder,
            "no-order-made": isInitialized.value && allocation.tableStatus == RoomPlanTableStatus.NoOrderMade,
        };
    });
    const guestsNumber = computed(() =>
        isInitialized.value ? (bill.currentBill?.manualEndUsersCount ?? allocation.guestsNumber) : 0
    );
    const tableAllocationTimestamp = computed(() => (isInitialized.value ? allocation.tableAllocationTimestamp : null));

    async function onBillsChanged(ev: IBillEvent) {
        if (ev.tableId == allocation.tableId) {
            await allocation.refreshGuestsInfo();
        }
    }

    onMounted(async () => {
        dataEvents.appSocket.BillOpened.add(onBillsChanged);
        dataEvents.appSocket.BillChanged.add(onBillsChanged);
        dataEvents.appSocket.BillNameChanged.add(onBillsChanged);
        dataEvents.appSocket.BillClosed.add(onBillsChanged);

        await allocation.refreshGuestsInfo();
        isInitialized.value = true;
    });

    onBeforeUnmount(() => {
        dataEvents.appSocket.BillOpened.remove(onBillsChanged);
        dataEvents.appSocket.BillChanged.remove(onBillsChanged);
        dataEvents.appSocket.BillNameChanged.remove(onBillsChanged);
        dataEvents.appSocket.BillClosed.remove(onBillsChanged);
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .table-allocation-info {
        display: inline-block;
        font-size: 16px;
        color: colors.$white;
        background-color: colors.$dark-gray-100;
        border-radius: 0.3125em;
        transition:
            color 0.3s variables.$easeOutExpo,
            background-color 0.3s variables.$easeOutExpo;
        padding: 0.375em 0.75em;

        -webkit-box-shadow: 1px 1px 1px 0px colors.$dark-gray-400;
        -moz-box-shadow: 1px 1px 1px 0px colors.$dark-gray-400;
        box-shadow: 1px 1px 1px 0px colors.$dark-gray-400;

        &.active {
            cursor: pointer;
        }

        .table-allocation-time {
            margin-right: 1.25em;
        }

        &.open-bill {
            background-color: colors.$red-500;

            &.active {
                &:hover,
                &:active {
                    background-color: colors.$red-700;
                }
            }
        }

        &.unfulfilled-order {
            background-color: colors.$dark-gray-500;

            &.active {
                &:hover,
                &:active {
                    background-color: colors.$dark-gray-700;
                }
            }
        }

        &.no-order-made {
            background-color: colors.$orange-500;

            &.active {
                &:hover,
                &:active {
                    background-color: colors.$orange-700;
                }
            }
        }
    }
</style>
